import { CaptionBold, Skeleton, Caption } from '@comeon/ui';
import React from 'react';
import styled from 'styled-components';
import { Translation } from '~/components/translation/types';
import SportsbookTextKeys from '~/components/sportsbook/lobby/sportsbook-text-keys';
import { AnimatedOddsDisplay } from './odds-display';
import Icon, { ICON_CIRCLE_EXCLAMATION_FILLED, ICON_GIFT_LINE, ICON_ROCKET } from '../../../icon/index';
import HighlightedLabel from './highlighted-label';
import EventNameInfo from '~/components/sportsbook/lobby/components/sportsbook-event-name-info';
import { OddsFormat } from '../sportsbook-types';

const StyledCaption = styled(Caption)`
	color: var(--main-text-supportive);
`;

const BetBuilderLabel = styled(CaptionBold)`
	color: var(--main-text);
	white-space: nowrap;
	min-width: fit-content;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
`;

const SelectionName = styled(CaptionBold)<{ isHighlighted?: boolean }>`
	color: var(--main-text);
	min-width: fit-content;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;

	${({ isHighlighted }) =>
		isHighlighted &&
		`
			color: var(--system-informative);
		`}
`;

const BetBuilderSelectionName = styled(Caption)`
	color: var(--main-text);
	min-width: fit-content;
	margin-left: 12px;
`;

const SelectionLabel = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;

	& > ${SelectionName}, & > ${BetBuilderLabel}, & > ${BetBuilderSelectionName} {
		align-self: center; // Ensures Names are centrally aligned with Addons when inside SelectionLabel
	}
`;

const SelectionLabelAddons = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const PointsLabel = styled(CaptionBold)`
	color: var(--main-text);
`;

const MarketName = styled(StyledCaption)`
	margin-top: 4px;
`;

const EventName = styled(StyledCaption)`
	word-break: break-word;
`;

const Wrapper = styled.div<{ isDisabled?: boolean }>`
	width: 100%;
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;

const BetBuilderAndPreCannedWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
`;

const LabelAndEventInfoWrapper = styled.div``;

const RestrictionIcon = styled(Icon).attrs({
	type: ICON_CIRCLE_EXCLAMATION_FILLED as string,
	width: '16px',
	height: '16px'
})`
	min-width: 16px;
	margin: 0 2px 0 4px;
	color: var(--system-informative);
`;

const OddsDisplay = styled(AnimatedOddsDisplay)``;

const InitOddsDisplay = styled(AnimatedOddsDisplay)`
	text-decoration: line-through;
`;

const PointsLabelWrapper = styled(HighlightedLabel)``;

const InvalidLabelWrapper = styled(HighlightedLabel)`
	padding: 2px 15px;
`;

const BetBuilderAndPreCannedSelection = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	margin: 0 10px 0 3px;
	align-items: flex-start;
	gap: 6px;
`;

const BetBuilderAndPreCannedSelectionNameWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	position: relative;

	${SelectionName} {
		flex: 1;
		margin-left: 12px;
	}
`;

const EventAdditionalInfoWrapper = styled.div``;

const EventInfoWrapper = styled.div<{ hasPaddingBottom?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 7px;
	flex-wrap: wrap;
	padding-bottom: ${({ hasPaddingBottom }) => hasPaddingBottom && '5px'};
`;

const BulletIndicator = styled.span`
	z-index: 1;
	margin-top: 4px;
	position: absolute;
	height: 7px;
	width: 7px;
	border-radius: 50%;
	border: 1px solid var(--main-text-supportive);
`;

const ConnectionBorder = styled.div`
	position: absolute;
	left: 3px;
	top: 10px;
	height: 100%;
	border-right: 1px solid var(--main-text-supportive);
`;

export const GiftIcon = styled(Icon)`
	&& {
		fill: var(--main-icons);
	}
`;

export const BetBoostIcon = styled(Icon)`
	transform: rotate(40deg);

	&& {
		fill: var(--main-icons);
	}
`;

type Props = {
	selectionName?: string;
	eventName?: string;
	marketName?: string;
	eventLink?: string;
	initOdds?: number;
	finalOdds: number;
	oddsFormat: OddsFormat;
	translations: Translation;
	points?: number;
	hasHighlightedOdds?: boolean;
	hasHighlightedPoints?: boolean;
	hasHighlightedSelectionName?: boolean;
	hasRestrictionIcon?: boolean;
	isBetBuilder?: boolean;
	isPreCanned?: boolean;
	hasGiftIcon?: boolean;
	hasBoostIcon?: boolean;
	shouldDisplayOdds?: boolean;
	renderAdditionalEventInfo?: () => React.ReactNode;
};

const SelectionInfo: React.FC<Props> = ({
	selectionName,
	eventName,
	marketName,
	eventLink,
	initOdds,
	finalOdds,
	oddsFormat,
	points,
	translations,
	hasHighlightedOdds = false,
	hasHighlightedPoints = false,
	hasHighlightedSelectionName = false,
	hasRestrictionIcon = false,
	isBetBuilder = false,
	isPreCanned = false,
	hasGiftIcon = false,
	hasBoostIcon = false,
	shouldDisplayOdds = true,
	renderAdditionalEventInfo
}) => {
	const selectionNames = selectionName?.split(/\s?[,&|]\s?/);
	return (
		<div>
			{isBetBuilder || isPreCanned ? (
				<Wrapper>
					<BetBuilderAndPreCannedWrapper>
						<LabelAndEventInfoWrapper>
							<SelectionLabel>
								<BetBuilderLabel data-at="betbuilder-label">
									{translations[SportsbookTextKeys.BET_BUILDER_BET_TITLE]}
									{hasBoostIcon && <BetBoostIcon type={ICON_ROCKET} width="24" height="24" />}
									{hasGiftIcon && <GiftIcon type={ICON_GIFT_LINE} width="24" height="24" />}
									<SelectionLabelAddons>
										{points && (
											<PointsLabelWrapper
												dataAt="selection-points"
												isHighlighted={hasHighlightedPoints}
											>
												<PointsLabel>{points}</PointsLabel>
											</PointsLabelWrapper>
										)}
										{shouldDisplayOdds && !!initOdds && initOdds < finalOdds && (
											<InitOddsDisplay
												odds={initOdds}
												oddsFormat={oddsFormat}
												dataAt="selection-init-odds"
												hasHighlightedOdds={false}
											/>
										)}
										{!shouldDisplayOdds && (
											<InvalidLabelWrapper isHighlighted={false}>-</InvalidLabelWrapper>
										)}
										{finalOdds > 0 && shouldDisplayOdds && (
											<OddsDisplay
												odds={finalOdds}
												oddsFormat={oddsFormat}
												hasHighlightedOdds={hasHighlightedOdds}
												dataAt="selection-odds"
											/>
										)}
										{hasRestrictionIcon && (
											<RestrictionIcon type={ICON_CIRCLE_EXCLAMATION_FILLED} />
										)}
									</SelectionLabelAddons>
								</BetBuilderLabel>
							</SelectionLabel>
							<EventInfoWrapper hasPaddingBottom={isBetBuilder || isPreCanned}>
								{eventName && eventName !== marketName && (
									<EventNameInfo eventName={eventName} eventLink={eventLink || ''} />
								)}
								{renderAdditionalEventInfo && (
									<EventAdditionalInfoWrapper>
										{renderAdditionalEventInfo()}
									</EventAdditionalInfoWrapper>
								)}
							</EventInfoWrapper>
						</LabelAndEventInfoWrapper>
					</BetBuilderAndPreCannedWrapper>

					{isBetBuilder && (
						<BetBuilderAndPreCannedSelection>
							{selectionNames?.map((item, index) => {
								const isLast = index === selectionNames.length - 1;
								return (
									// eslint-disable-next-line react/no-array-index-key
									<BetBuilderAndPreCannedSelectionNameWrapper key={index}>
										<BulletIndicator />
										{!isLast && <ConnectionBorder />}
										<BetBuilderSelectionName data-at="selection-name">
											{item}
										</BetBuilderSelectionName>
									</BetBuilderAndPreCannedSelectionNameWrapper>
								);
							})}
						</BetBuilderAndPreCannedSelection>
					)}
					{isPreCanned && (
						<BetBuilderAndPreCannedSelection>
							{selectionNames?.map((item, index) => {
								const isLast = index === selectionNames.length - 1;
								return (
									// eslint-disable-next-line react/no-array-index-key
									<BetBuilderAndPreCannedSelectionNameWrapper key={index}>
										<BulletIndicator />
										{!isLast && <ConnectionBorder />}
										<BetBuilderSelectionName data-at="selection-name">
											{item}
										</BetBuilderSelectionName>
									</BetBuilderAndPreCannedSelectionNameWrapper>
								);
							})}
						</BetBuilderAndPreCannedSelection>
					)}
				</Wrapper>
			) : (
				<Wrapper>
					<SelectionLabel>
						<SelectionName isHighlighted={hasHighlightedSelectionName} data-at="selection-name">
							{selectionName}
							{hasBoostIcon && <BetBoostIcon type={ICON_ROCKET} width="24" height="24" />}
							{hasGiftIcon && <GiftIcon type={ICON_GIFT_LINE} width="24" height="24" />}
							<SelectionLabelAddons>
								{points && (
									<PointsLabelWrapper dataAt="selection-points" isHighlighted={hasHighlightedPoints}>
										<PointsLabel>{points}</PointsLabel>
									</PointsLabelWrapper>
								)}
								{!!initOdds && initOdds < finalOdds && (
									<InitOddsDisplay
										odds={initOdds}
										oddsFormat={oddsFormat}
										dataAt="selection-init-odds"
										hasHighlightedOdds={false}
									/>
								)}
								{!shouldDisplayOdds && (
									<InvalidLabelWrapper isHighlighted={false}>-</InvalidLabelWrapper>
								)}
								{finalOdds > 0 && shouldDisplayOdds && (
									<OddsDisplay
										odds={finalOdds}
										oddsFormat={oddsFormat}
										hasHighlightedOdds={hasHighlightedOdds}
										dataAt="selection-odds"
									/>
								)}
								{hasRestrictionIcon && <RestrictionIcon type={ICON_CIRCLE_EXCLAMATION_FILLED} />}
							</SelectionLabelAddons>
						</SelectionName>
					</SelectionLabel>
					<MarketName data-at="market-name">{marketName}</MarketName>
					<EventInfoWrapper>
						{eventName && eventName !== marketName && (
							<EventNameInfo eventName={eventName} eventLink={eventLink || ''} />
						)}
						{renderAdditionalEventInfo && (
							<EventAdditionalInfoWrapper>{renderAdditionalEventInfo()}</EventAdditionalInfoWrapper>
						)}
					</EventInfoWrapper>
				</Wrapper>
			)}
		</div>
	);
};

export const SkeletonSelectionInfo = () => (
	<Wrapper>
		<Skeleton width="60%" height="auto" margin="0 0 4px 0">
			<SelectionName data-at="selection-name">Betslip line</SelectionName>
		</Skeleton>
		<Skeleton width="40%" height="12px" margin="0 0 2px 0">
			<MarketName data-at="market-name">Market name</MarketName>
		</Skeleton>
		<Skeleton width="70%" height="12px">
			<EventName data-at="event-name">Event name</EventName>
		</Skeleton>
	</Wrapper>
);

export default SelectionInfo;
